import dayjs from 'dayjs';
import { AppSettings, Client, LessonStatus, Reservation, Slot, Student } from '@opanuj/shared/types';
import { Database, get, push, ref, update } from 'firebase/database';
import { generateLessonsTimestamps } from '@opanuj/shared/lessons';

type ReservationAcceptOptions = {
	slotId?: string;
	slotIds?: string[];
	clientUid?: string;
	clientId?: string;
	reservationId?: string;
	reservationIds?: string[];
	firstDay?: string;
	lastDay?: string;
};

const getClientByUid = async (database: Database, uid?: string): Promise<Client | null> => {
	if (!uid) {
		return null;
	}

	const clientsSnapshot = await get(ref(database, 'clients'));
	const clients = clientsSnapshot.val() ?? {};
	const match = Object.values(clients as Record<string, Client>).find((client) => client?.uid === uid);
	return match ?? null;
};

const getClientById = async (database: Database, clientId?: string): Promise<Client | null> => {
	if (!clientId) {
		return null;
	}

	const snapshot = await get(ref(database, `clients/${clientId}`));
	const value = snapshot.val();
	return value ? (value as Client) : null;
};

export const reservationsAccept = async (
	database: Database,
	{
		slotId,
		slotIds,
		clientUid,
		clientId,
		reservationId,
		reservationIds = [],
		firstDay,
		lastDay,
	}: ReservationAcceptOptions,
): Promise<void> => {
	const slotIdsToProcess = Array.from(new Set([...(slotIds ?? []), ...(slotId ? [slotId] : [])].filter(Boolean) as string[]));
	if (!slotIdsToProcess.length || !firstDay || !lastDay) {
		return;
	}

	const bookingsSnapshot = await get(ref(database, 'bookings'));
	const bookings = (bookingsSnapshot.val() ?? {}) as Record<string, Reservation>;
	const matchingReservations: Reservation[] = Object.values(bookings).filter((booking) => {
		const matchesExplicitReservation = Boolean(
			(reservationId && booking.id === reservationId)
			|| (reservationIds.length && reservationIds.includes(booking.id)),
		);
		if (matchesExplicitReservation) {
			return true;
		}

		const matchesClient = Boolean(
			(clientUid && booking.uid === clientUid)
			|| (clientId && booking.clientId === clientId),
		);
		return booking.lessons.some((lesson) => slotIdsToProcess.includes(lesson.slot)) && matchesClient;
	});

	if (!matchingReservations.length) {
		return;
	}

	const resolvedClient = clientId
		? await getClientById(database, clientId)
		: await getClientByUid(database, clientUid ?? matchingReservations[0]?.uid);
	const clientIdForProducts = resolvedClient?.id ?? clientId;

	const updates: Record<string, any> = {};
	const productEntries: Array<{ slotId: string; student: Student }> = [];
	const defaultDaysOffSnapshot = await get(ref(database, 'settings/daysOff'));
	const daysOff: AppSettings['daysOff'] = defaultDaysOffSnapshot.val() ?? [];

	for (const slotIdToProcess of slotIdsToProcess) {
		const slotSnapshot = await get(ref(database, `slots/${slotIdToProcess}`));
		const slot = slotSnapshot.val() as (Slot & { lessons?: Array<Record<string, any>> }) | null;
		if (!slot) {
			continue;
		}

		const slotWithLessons = slot as Slot & { lessons?: Array<Record<string, any>> };
		const participants = matchingReservations.flatMap((reservation) => {
			return (reservation.lessons ?? [])
				.filter((lesson) => lesson.slot === slotIdToProcess)
				.map((lesson) => ({
					...lesson.student,
					uid: reservation.uid,
					clientId: reservation.clientId ?? clientIdForProducts,
					status: LessonStatus.Regular,
				}));
		});

		const deduplicatedParticipants = participants.filter((participant, index, array) => {
			return array.findIndex((candidate) => candidate.uid === participant.uid && candidate.name === participant.name) === index;
		});

		if (deduplicatedParticipants.length > 0 && clientIdForProducts) {
			for (const participant of deduplicatedParticipants) {
				productEntries.push({
					slotId: slotIdToProcess,
					student: {
						...participant,
						uid: participant.uid,
						clientId: clientIdForProducts,
					},
				});
			}
		}

		const timestamps = generateLessonsTimestamps({
			start: dayjs(firstDay).startOf('day'),
			end: dayjs(lastDay).endOf('day'),
			daysOff: daysOff.map(dayjs),
			day: slot.day,
			time: slot.time,
		});

		const lessonsSnapshot = await get(ref(database, 'lessons'));
		const existingLessons = (lessonsSnapshot.val() ?? {}) as Record<string, any>;
		const startedLessonEntries = Object.entries(existingLessons).filter(([, lesson]) => lesson?.sourceSlot === slotIdToProcess);

		if (slot.started && startedLessonEntries.length > 0) {
			const updatedStudentsByLessonId = new Map<string, any[]>();
			for (const [lessonId, existingLesson] of startedLessonEntries) {
				const students = Array.isArray(existingLesson.students) ? existingLesson.students : [];
				const newStudents = deduplicatedParticipants
					.filter((participant) => !students.some((student: any) => student.uid === participant.uid && student.name === participant.name))
					.map((participant) => ({ ...participant, status: LessonStatus.Regular }));
				if (newStudents.length > 0) {
					const updatedStudents = [...students, ...newStudents];
					updates[`lessons/${lessonId}/students`] = updatedStudents;
					updatedStudentsByLessonId.set(lessonId, updatedStudents);
				}
			}
			const generatedLessons = startedLessonEntries.map(([lessonId, lesson]) => ({
				id: lessonId,
				...lesson,
				...(updatedStudentsByLessonId.has(lessonId)
					? { students: updatedStudentsByLessonId.get(lessonId) }
					: {}),
			}));
			updates[`slots/${slotIdToProcess}/started`] = true;
			updates[`slots/${slotIdToProcess}/lessons`] = Array.isArray(slotWithLessons.lessons)
				? slotWithLessons.lessons.map((lesson: any) => {
					const updatedStudents = updatedStudentsByLessonId.get(lesson.id);
					return updatedStudents ? { ...lesson, students: updatedStudents } : lesson;
				})
				: generatedLessons;
		} else {
			const newLessons = timestamps.map((ts) => ({
				sourceSlot: slotIdToProcess,
				status: LessonStatus.Regular,
				code: slot.code,
				teacher: slot.defaultTeacher,
				timestamp: ts.unix(),
				duration: slot.duration,
				firstDay,
				lastDay,
				location: slot.location,
				room: slot.room,
				students: deduplicatedParticipants,
			}));

			const createdLessons = await Promise.all(
				newLessons.map((lesson) => push(ref(database, 'lessons'), lesson)),
			);

			const persistedLessons = createdLessons.map((lessonRef, index) => ({
				id: lessonRef.key,
				...newLessons[index],
			}));

			updates[`slots/${slotIdToProcess}/started`] = true;
			updates[`slots/${slotIdToProcess}/lessons`] = Array.isArray(slotWithLessons.lessons)
				? [...slotWithLessons.lessons, ...persistedLessons]
				: persistedLessons;
		}

		const slotReservations = Array.isArray(slot.reservations) ? slot.reservations : [];
		const reservationIdsForSlot = new Set(matchingReservations
			.filter((reservation) => reservation.lessons.some((lesson) => lesson.slot === slotIdToProcess))
			.map((reservation) => reservation.id));
		slot.reservations = slotReservations.filter((reservation: any) => !reservationIdsForSlot.has(reservation?.reservationId ?? reservation?.id));
		updates[`slots/${slotIdToProcess}/reservations`] = slot.reservations;
	}

	if (clientIdForProducts) {
		for (const productEntry of productEntries) {
			await push(ref(database, `clients/${clientIdForProducts}/products`), productEntry);
		}
	}

	if (Object.keys(updates).length > 0) {
		await update(ref(database), updates);
	}

	const bookingEntries = (await get(ref(database, 'bookings'))).val() ?? {};
	const bookingUpdates: Record<string, any> = {};
	for (const [bookingKey, booking] of Object.entries(bookingEntries as Record<string, Reservation>)) {
		const isMatchingReservation = matchingReservations.some((reservation) => reservation.id === booking.id);
		const remainingLessons = (booking.lessons ?? []).filter((lesson) => {
			return !isMatchingReservation || !slotIdsToProcess.includes(lesson.slot);
		});

		if (!isMatchingReservation || remainingLessons.length === (booking.lessons ?? []).length) {
			continue;
		}

		bookingUpdates[`bookings/${bookingKey}`] = remainingLessons.length ? { ...booking, lessons: remainingLessons } : null;
	}

	if (Object.keys(bookingUpdates).length > 0) {
		await update(ref(database), bookingUpdates);
	}

};

export const reservationsAcceptLegacy = reservationsAccept;